import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { nanoid } from "nanoid";
import { useEffect, useRef, useState } from "react";
import { sharedMutators } from "../../../shared/mutators";
import {
  isPersonalChatAutoTidyByEntries,
  isPersonalChatInactiveByEntries,
  lastPersonalChatMessageAt,
} from "../../../shared/personalChatInactivity";
import { queries } from "../../../shared/queries";
import { config } from "../../environment";
import { useAdminChatView } from "../../hooks/useAdminChatView";
import { useQuery } from "../../hooks/useQuery";
import { downloadChatAsJson } from "../../lib/downloadChat";
import { apiUrl } from "../../lib/tenantOrigin";
import { navigate, routes, useCurrentUrl } from "../../lib/urls";
import { Backdrop } from "../Backdrop";
import { useToast } from "../Toast";
import { ChatTabBar } from "./ChatTabBar";
import { nextTabIdAfterClose } from "./nextTabIdAfterClose";
import { resolveActiveTab } from "./resolveActiveTab";
import { ChatWorkspace } from "./ChatWorkspace";
import { chatLabel } from "./pickChatIconColor";
import { CloseAllPersonalChatsModal } from "./CloseAllPersonalChatsModal";
import { PersonalChatHistoryModal } from "./PersonalChatHistoryModal";
import { TidyUpPersonalChatsModal } from "./TidyUpPersonalChatsModal";

const Container = styled("div")`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 16px;
  box-shadow: var(--elevation-low);
  overflow: hidden;
`;

type Pending = { id: string; chatId: string };

export function PersonalChats() {
  const z = useZero();
  const { tabId, history } = useCurrentUrl(routes.chats);
  const { isAdminView, userId } = useAdminChatView();
  const { showToast } = useToast();

  const [tabs, tabsResult] = useQuery(
    queries.personalChats(isAdminView ? { userId } : undefined)
  );
  const [closedChats, closedChatsResult] = useQuery(
    queries.closedPersonalChats(isAdminView ? { userId } : undefined)
  );

  const [pendings, setPendings] = useState<Pending[]>([]);
  const [tidyUpOpen, setTidyUpOpen] = useState(false);
  const [closeAllOpen, setCloseAllOpen] = useState(false);
  const [reopeningTabId, setReopeningTabId] = useState<string | null>(null);

  const realTab = tabs.find((t) => t.id === tabId);

  useEffect(() => {
    setPendings((prev) => {
      const next = prev.filter((p) => !tabs.some((t) => t.id === p.id));
      return next.length === prev.length ? prev : next;
    });
  }, [tabs]);

  // Automatically close chats abandoned for longer than the auto-tidy window,
  // once per visit and never the pinned or currently-open chat.
  const didAutoTidy = useRef(false);
  useEffect(() => {
    if (isAdminView || didAutoTidy.current) return;
    if (tabsResult.type !== "complete" || !tabId) return;
    didAutoTidy.current = true;
    const now = Date.now();
    for (const t of tabs) {
      if (
        t.pinnedAt == null &&
        t.id !== tabId &&
        isPersonalChatAutoTidyByEntries(t.chat?.entries, t.createdAt, now)
      ) {
        z.mutate(sharedMutators.closePersonalChat({ id: t.id }));
      }
    }
  }, [tabsResult.type, tabId, tabs, isAdminView, z]);

  const activePending = pendings.find((p) => p.id === tabId) ?? null;

  const activeName = realTab?.name ?? (activePending ? "Untitled" : undefined);
  const tabTitle =
    activeName === undefined
      ? config.title
      : `${activeName === "Untitled" ? "New chat" : activeName} • ${config.title}`;

  useEffect(() => {
    if (reopeningTabId && realTab?.id === reopeningTabId) {
      setReopeningTabId(null);
    }
  }, [reopeningTabId, realTab]);

  useEffect(() => {
    const action = resolveActiveTab({
      tabId,
      tabs: tabs.map((t) => ({
        id: t.id,
        lastActiveAt: t.lastActiveAt,
        started: (t.chat?.entries.length ?? 0) > 0,
      })),
      pendings,
      isAdminView,
      tabsLoaded: tabsResult.type === "complete",
      reopeningTabId,
    });
    if (action.type === "stay") return;
    if (action.type === "navigate") {
      navigate(
        routes.chats,
        { tabId: action.tabId, userId },
        { replace: true }
      );
      return;
    }
    const next: Pending = { id: nanoid(12), chatId: nanoid(12) };
    z.mutate(
      sharedMutators.createPersonalChat({ id: next.id, chatId: next.chatId })
    );
    setPendings([next]);
    navigate(routes.chats, { tabId: next.id, userId }, { replace: true });
  }, [
    tabs,
    pendings,
    isAdminView,
    userId,
    tabsResult.type,
    reopeningTabId,
    tabId,
    z,
  ]);

  const newTab = () => {
    const next: Pending = { id: nanoid(12), chatId: nanoid(12) };
    z.mutate(
      sharedMutators.createPersonalChat({ id: next.id, chatId: next.chatId })
    );
    setPendings((prev) => [...prev, next]);
    navigate(routes.chats, { tabId: next.id });
  };

  const navigateAfterClose = (closedIds: string[]) => {
    const nextId = nextTabIdAfterClose({ tabs, pendings, closedIds });
    navigate(routes.chats, { tabId: nextId ?? "", userId }, { replace: true });
  };

  const closeTab = (id: string) => {
    if (pendings.some((p) => p.id === id)) {
      setPendings((prev) => prev.filter((p) => p.id !== id));
    } else {
      z.mutate(sharedMutators.closePersonalChat({ id }));
    }
    if (id === tabId) {
      navigateAfterClose([id]);
    }
  };

  const reopenChat = (id: string) => {
    z.mutate(sharedMutators.reopenPersonalChat({ id }));
    setReopeningTabId(id);
    navigate(routes.chats, { tabId: id, userId });
  };

  const sortedTabs = [...tabs].sort((a, b) => {
    const aPinned = a.pinnedAt != null;
    const bPinned = b.pinnedAt != null;
    if (aPinned !== bPinned) return aPinned ? -1 : 1;
    return (a.position ?? a.createdAt) - (b.position ?? b.createdAt);
  });
  const realTabItems = sortedTabs.map((t) => ({
    id: t.id,
    name: t.name,
    lastActivityAt: lastPersonalChatMessageAt(t.chat?.entries, t.createdAt),
    pinned: t.pinnedAt != null,
    position: t.position ?? t.createdAt,
    pending: false,
    started: (t.chat?.entries.length ?? 0) > 0,
  }));
  const tabsForBar = isAdminView
    ? realTabItems
    : [
        ...realTabItems,
        ...pendings
          .filter((p) => !tabs.some((t) => t.id === p.id))
          .map((p) => ({
            id: p.id,
            name: "Untitled",
            lastActivityAt: null,
            pinned: false,
            position: Number.MAX_SAFE_INTEGER,
            pending: true,
            started: false,
          })),
      ];

  const activeTabId = isAdminView
    ? (realTab?.id ?? null)
    : (realTab?.id ?? activePending?.id ?? null);

  // The query filters closedAt IS NOT null, but the row type stays nullable.
  const closedForBar = closedChats.map((c) => ({
    id: c.id,
    name: c.name,
    closedAt: c.closedAt ?? 0,
    lastActivityAt: lastPersonalChatMessageAt(c.chat?.entries, c.createdAt),
  }));

  const workspace = realTab?.chat
    ? { id: realTab.id, chat: realTab.chat, pending: false }
    : !isAdminView && activePending
      ? {
          id: activePending.id,
          chat: {
            id: activePending.chatId,
            respondingAt: null,
            lastLoopHeartbeatAt: null,
            awaitingAction: null,
            entries: [],
          },
          pending: true,
        }
      : null;

  const staleTabs = tabs.filter(
    (t) =>
      t.pinnedAt == null &&
      t.id !== tabId &&
      isPersonalChatInactiveByEntries(t.chat?.entries, t.createdAt)
  );

  const tidyUp = () => {
    const closedIds = staleTabs.map((t) => t.id);
    for (const id of closedIds) {
      z.mutate(sharedMutators.closePersonalChat({ id }));
    }
    if (closedIds.some((id) => id === tabId)) {
      navigateAfterClose(closedIds);
    }
    setTidyUpOpen(false);
  };

  const closeAll = () => {
    const closedIds = [
      ...tabs.filter((t) => t.pinnedAt == null).map((t) => t.id),
      ...pendings.map((p) => p.id),
    ];
    for (const chat of tabs) {
      if (chat.pinnedAt != null) continue;
      z.mutate(sharedMutators.closePersonalChat({ id: chat.id }));
    }
    setPendings([]);
    setCloseAllOpen(false);
    if (closedIds.some((id) => id === tabId)) {
      navigateAfterClose(closedIds);
    }
  };

  const forkChat = (id: string) => {
    const tab = tabs.find((t) => t.id === id);
    if (!tab?.chat) return;
    const entryIds: Record<string, string> = {};
    const attachmentIds: Record<string, string> = {};
    for (const entry of tab.chat.entries) {
      if (entry.streaming) continue;
      entryIds[entry.id] = nanoid(12);
      for (const attachment of entry.attachments) {
        attachmentIds[attachment.id] = nanoid(12);
      }
    }
    const newId = nanoid(12);
    z.mutate(
      sharedMutators.forkPersonalChat({
        id: newId,
        chatId: nanoid(12),
        sourceChatId: tab.chat.id,
        entryIds,
        attachmentIds,
      })
    );
    navigate(routes.chats, { tabId: newId, userId });
  };

  const downloadChat = (id: string) => {
    const tab = tabs.find((t) => t.id === id);
    if (!tab?.chat) return;
    downloadChatAsJson(tab.chat.entries, chatLabel(tab.name), apiUrl()).catch(
      () => {
        showToast({
          icon: "circle-off",
          message: "Failed to download chat",
          buttonLabel: "Dismiss",
        });
      }
    );
  };

  const reorderChat = (id: string, position: number, pinned: boolean) => {
    const wasPinned = tabs.find((t) => t.id === id)?.pinnedAt != null;
    if (pinned !== wasPinned) {
      z.mutate(sharedMutators.setPersonalChatPinned({ id, pinned, position }));
    } else {
      z.mutate(sharedMutators.reorderPersonalChat({ id, position }));
    }
  };

  const togglePin = (id: string) => {
    const tab = tabs.find((t) => t.id === id);
    if (!tab) return;
    if (tab.pinnedAt != null) {
      z.mutate(
        sharedMutators.setPersonalChatPinned({ id: tab.id, pinned: false })
      );
      return;
    }
    const minPosition = Math.min(...tabs.map((t) => t.position ?? t.createdAt));
    z.mutate(
      sharedMutators.setPersonalChatPinned({
        id: tab.id,
        pinned: true,
        position: minPosition - 1000,
      })
    );
  };

  return (
    <>
      <title>{tabTitle}</title>
      <Backdrop permanentNavigation>
        <Container>
          <ChatTabBar
            tabs={tabsForBar}
            activeTabId={activeTabId}
            userId={userId}
            closedChats={closedForBar}
            onClose={closeTab}
            onFork={isAdminView ? undefined : forkChat}
            onDownload={isAdminView ? undefined : downloadChat}
            onTogglePin={isAdminView ? undefined : togglePin}
            onNewTab={newTab}
            onTidyUp={
              !isAdminView && staleTabs.length > 0
                ? () => setTidyUpOpen(true)
                : undefined
            }
            onCloseAll={
              !isAdminView && tabs.some((t) => t.pinnedAt == null)
                ? () => setCloseAllOpen(true)
                : undefined
            }
            onShowHistory={
              closedChats.length > 0
                ? () => navigate(routes.chats, { tabId, userId, history: "1" })
                : undefined
            }
            onReopenChat={isAdminView ? undefined : reopenChat}
            onReorder={isAdminView ? undefined : reorderChat}
            readOnly={isAdminView}
          />
          <TidyUpPersonalChatsModal
            open={tidyUpOpen}
            onClose={() => setTidyUpOpen(false)}
            staleChats={staleTabs.map((t) => ({
              id: t.id,
              name: t.name,
            }))}
            onCloseAll={tidyUp}
            onOpenChat={(id) => {
              navigate(routes.chats, { tabId: id, userId });
              setTidyUpOpen(false);
            }}
          />
          <CloseAllPersonalChatsModal
            open={closeAllOpen}
            onClose={() => setCloseAllOpen(false)}
            chats={tabs
              .filter((t) => t.pinnedAt == null)
              .map((t) => ({ id: t.id, name: t.name }))}
            onCloseAll={closeAll}
            onOpenChat={(id) => {
              navigate(routes.chats, { tabId: id, userId });
              setCloseAllOpen(false);
            }}
          />
          <PersonalChatHistoryModal
            open={Boolean(history)}
            onClose={() =>
              navigate(
                routes.chats,
                { tabId, userId, history: "" },
                { replace: true }
              )
            }
            readOnly={isAdminView}
            closedChats={closedChats}
            closedChatsComplete={closedChatsResult.type === "complete"}
            onRecover={(ids) => {
              if (!isAdminView) {
                for (const id of ids) {
                  z.mutate(sharedMutators.reopenPersonalChat({ id }));
                }
              }
              const first = ids[0];
              if (first) {
                if (!isAdminView) setReopeningTabId(first);
                navigate(
                  routes.chats,
                  { tabId: first, userId, history: "" },
                  { replace: true }
                );
              }
            }}
          />
          {workspace && (
            <ChatWorkspace
              key={workspace.id}
              personalChatId={workspace.id}
              chat={workspace.chat}
              pending={workspace.pending}
              readOnly={isAdminView}
              onForkChat={
                !isAdminView && realTab?.chat
                  ? () => forkChat(realTab.id)
                  : undefined
              }
              onDownloadChat={
                !isAdminView && realTab?.chat
                  ? () => downloadChat(realTab.id)
                  : undefined
              }
              onCloseChat={
                !isAdminView && realTab ? () => closeTab(realTab.id) : undefined
              }
              onTogglePin={
                !isAdminView && realTab?.chat
                  ? () => togglePin(realTab.id)
                  : undefined
              }
              pinned={realTab?.pinnedAt != null}
            />
          )}
        </Container>
      </Backdrop>
    </>
  );
}
